/**
 * @module components/PledgeForm
 * @description The pledge card back, as an accessible, mobile-first form.
 * Sections 1–4 mirror the print card field-for-field so paper and digital
 * pledges share one schema (domain/types.ts → Pledge). Amounts, options and
 * wording come from the active edition (config/editions.ts); the printed
 * pledge card (components/CardBacks.tsx) reads the same config.
 *
 * Payment is never collected here. "Paid tonight" donors are handed to
 * AFJ's existing Neon CRM form, keeping this feature outside PCI scope.
 */
import { useId, useState, type FormEvent } from "react";
import { CAMPAIGN, CAUSE_LABELS, formatUsd } from "../config/campaign";
import type { EditionConfig } from "../config/editions";
import { pledgeRepository } from "../data";
import { hrefFor } from "../app/router";
import { useEdition } from "../app/edition";
import type { CauseArea, Pledge } from "../domain/types";
import { validatePledge, type PledgeDraft, type PledgeErrors } from "../domain/pledgeValidation";

const EMPTY_DRAFT = (sourceCardSlug: string | null, edition: EditionConfig): PledgeDraft => ({
    amount: 0,
    frequency: "one-time",
    paymentIntent: edition.pledge.defaultIntent,
    name: "",
    email: "",
    phone: "",
    anonymous: false,
    tableNumber: "",
    causes: [],
    wantsMajorGiftConversation: false,
    sourceCardSlug,
    agreedToTerms: false,
    edition: edition.id,
});

export interface PledgeFormProps {
    sourceCardSlug: string | null;
    onSubmitted: (pledge: Pledge) => void;
}

export function PledgeForm({ sourceCardSlug, onSubmitted }: PledgeFormProps) {
    const uid = useId();
    const edition = useEdition();
    const { pledge: copy } = edition;
    const [draft, setDraft] = useState<PledgeDraft>(() => EMPTY_DRAFT(sourceCardSlug, edition));
    const [otherAmount, setOtherAmount] = useState("");
    const [errors, setErrors] = useState<PledgeErrors>({});
    const [storageError, setStorageError] = useState<string | null>(null);

    const set = <K extends keyof PledgeDraft>(key: K, value: PledgeDraft[K]) =>
        setDraft((d) => ({ ...d, [key]: value }));

    const toggleCause = (cause: CauseArea) =>
        set("causes", draft.causes.includes(cause) ? draft.causes.filter((c) => c !== cause) : [...draft.causes, cause]);

    const pickPreset = (amount: number) => {
        setOtherAmount("");
        set("amount", amount);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const found = validatePledge(draft, { requireTerms: copy.requireTerms });
        setErrors(found);
        if (Object.keys(found).length > 0) return;
        const pledge: Pledge = { ...draft, id: crypto.randomUUID(), createdAt: new Date().toISOString() };
        try {
            pledgeRepository.add(pledge);
            onSubmitted(pledge);
        } catch (error) {
            setStorageError(error instanceof Error ? error.message : String(error));
        }
    };

    // Doubling is shown only where a match exists (Gala edition).
    const doubled = edition.showMatch && draft.amount > 0 ? formatUsd(draft.amount * 2) : null;

    return (
        <form className="pledge-form" onSubmit={handleSubmit} noValidate>
            <fieldset className="pf-section">
                <legend className="pf-legend">
                    <span>1</span> {copy.amountLegend}
                </legend>
                <div className="pf-amounts" role="radiogroup" aria-label="Gift amount">
                    {copy.presetAmounts.map((amount) => (
                        <button
                            key={amount}
                            type="button"
                            role="radio"
                            aria-checked={draft.amount === amount && !otherAmount}
                            className="pf-amount"
                            onClick={() => pickPreset(amount)}
                        >
                            {formatUsd(amount)}
                        </button>
                    ))}
                    <label className="pf-amount pf-other">
                        <span>Other $</span>
                        <input
                            inputMode="numeric"
                            value={otherAmount}
                            placeholder="Amount"
                            onChange={(e) => {
                                const digits = e.target.value.replace(/[^\d]/g, "");
                                setOtherAmount(digits);
                                set("amount", Number(digits || 0));
                            }}
                        />
                    </label>
                </div>
                {errors.amount && <p className="pf-error">{errors.amount}</p>}
                {doubled && (
                    <p className="pf-doubled" aria-live="polite">
                        With the match, your gift becomes <strong>{doubled}</strong> for Jamaica.
                    </p>
                )}
                <label className="pf-check">
                    <input
                        type="checkbox"
                        checked={draft.frequency === "monthly"}
                        onChange={(e) => set("frequency", e.target.checked ? "monthly" : "one-time")}
                    />
                    <span><strong>Make it monthly</strong> and give all year long.</span>
                </label>
            </fieldset>

            <fieldset className="pf-section">
                <legend className="pf-legend">
                    <span>2</span> How I'll give
                </legend>
                <div className="pf-grid2">
                    {copy.intents.map(({ intent, label }) => (
                        <label key={intent} className="pf-check">
                            <input
                                type="radio"
                                name={`${uid}-intent`}
                                checked={draft.paymentIntent === intent}
                                onChange={() => set("paymentIntent", intent)}
                            />
                            <span>{label}</span>
                        </label>
                    ))}
                </div>
            </fieldset>

            <fieldset className="pf-section">
                <legend className="pf-legend">
                    <span>3</span> My details
                </legend>
                <label className="pf-field">
                    <span>Name</span>
                    <input autoComplete="name" value={draft.name} onChange={(e) => set("name", e.target.value)} aria-invalid={!!errors.name} />
                </label>
                {errors.name && <p className="pf-error">{errors.name}</p>}
                <div className="pf-grid2">
                    <label className="pf-field">
                        <span>Email</span>
                        <input type="email" autoComplete="email" value={draft.email} onChange={(e) => set("email", e.target.value)} aria-invalid={!!errors.email} />
                    </label>
                    <label className="pf-field">
                        <span>Phone</span>
                        <input type="tel" autoComplete="tel" value={draft.phone} onChange={(e) => set("phone", e.target.value)} />
                    </label>
                </div>
                {errors.email && <p className="pf-error">{errors.email}</p>}
                <div className="pf-grid2 pf-grid2--end">
                    <label className="pf-check">
                        <input type="checkbox" checked={draft.anonymous} onChange={(e) => set("anonymous", e.target.checked)} />
                        <span>Please keep my gift anonymous</span>
                    </label>
                    {copy.showTableNumber && (
                        <label className="pf-field pf-field--inline">
                            <span>Table</span>
                            <input value={draft.tableNumber} onChange={(e) => set("tableNumber", e.target.value)} />
                        </label>
                    )}
                </div>
            </fieldset>

            <fieldset className="pf-section">
                <legend className="pf-legend">
                    <span>4</span> What's closest to your heart?
                </legend>
                <div className="pf-causes">
                    {(Object.keys(CAUSE_LABELS) as CauseArea[]).map((cause) => (
                        <label key={cause} className="pf-check">
                            <input type="checkbox" checked={draft.causes.includes(cause)} onChange={() => toggleCause(cause)} />
                            <span>{CAUSE_LABELS[cause]}</span>
                        </label>
                    ))}
                </div>
                <label className="pf-check">
                    <input
                        type="checkbox"
                        checked={draft.wantsMajorGiftConversation}
                        onChange={(e) => set("wantsMajorGiftConversation", e.target.checked)}
                    />
                    <span><strong>I'd love to talk with AFJ about a larger or multi-year gift.</strong></span>
                </label>
            </fieldset>

            {copy.requireTerms && (
                <>
                    <label className="pf-check">
                        <input
                            type="checkbox"
                            checked={!!draft.agreedToTerms}
                            onChange={(e) => set("agreedToTerms", e.target.checked)}
                            aria-invalid={!!errors.agreedToTerms}
                        />
                        <span>
                            I agree to the <a href={hrefFor({ name: "terms" })} target="_blank" rel="noopener">Terms and Conditions</a>
                        </span>
                    </label>
                    {errors.agreedToTerms && <p className="pf-error">{errors.agreedToTerms}</p>}
                </>
            )}
            {storageError && <p className="pf-error" role="alert">{storageError}</p>}
            <button type="submit" className="btn btn--primary btn--block">
                {copy.submitLabel(draft.amount)}
            </button>
            <p className="pf-fine">
                Every gift to {CAMPAIGN.campaignName} goes where Jamaica needs it most, from the next school roof to a child's first
                eye exam.
            </p>
        </form>
    );
}
